import { Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { Progress, DownloadMode, DownloadManagerItemState, DownloadStatus, DownloadAllStatus } from '../reducer';
import LocalStorageManager from '../../../utils/localStorage/manager';
import { Api } from '../../../typings/api';
import Utils, { downloadsFolder, tempFolder } from '../../../utils';
import NativeApi from '../../../utils/nativeApi';
import { DriveItemInfo } from '../../DriveItemList/reducer';
import { createNotification } from '../../NotificationBar/actions';
import {
	DownloadManagerActionKeys, LocalStorageDownloadInfos, AddItemAction, UpdateStatusAction, UpdateDownloadAction,
	SetRequestIdAction, RemoveDownloadAction, SetDeleteAllStatusAction, SetIsDownloadingAllAction,
	SetDownloadAllStatusAction
} from './typings';
const Q = require('q');

const setIsDownloadingAll = (isDownloadingAll: boolean): SetIsDownloadingAllAction => ({
	type: DownloadManagerActionKeys.SET_IS_DOWNLOADING_ALL,
	isDownloadingAll
});

const setDownloadAllStatus = (status: DownloadAllStatus): SetDownloadAllStatusAction => {
	LocalStorageManager.setDownloadAllStatus(status);
	return {
		type: DownloadManagerActionKeys.SET_DOWNLOAD_ALL_STATUS,
		status
	};
};

const startDownloadAll = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { downloadManager: { downloadAllStatus } } = getState();
	dispatch(setIsDownloadingAll(true));
	if (downloadAllStatus === DownloadAllStatus.NotStarted) {
		dispatch(setDownloadAllStatus(DownloadAllStatus.Started));
	}
};

const finishDownloadAll = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	dispatch(setIsDownloadingAll(false));
	dispatch(setDownloadAllStatus(DownloadAllStatus.Finished));
};

const setIsDeletingAllStatus = (isDeletingAll: boolean): SetDeleteAllStatusAction => ({
	type: DownloadManagerActionKeys.SET_DELETE_ALL_STATUS,
	isDeletingAll
});

const loadPreviousDownloads = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const previousDownloads = LocalStorageManager.getDownloads();
	const previousDownloadAllStatus = LocalStorageManager.getDownloadAllStatus();
	if (previousDownloads) {
		dispatch({
			type: DownloadManagerActionKeys.INIT_DOWNLOADS,
			downloads: previousDownloads,
		});
	}
	if (previousDownloadAllStatus) {
		dispatch(setDownloadAllStatus(previousDownloadAllStatus));
	}
};

const addItemInManager = (driveItem: Api.DriveItem, mode: DownloadMode, dependencies: number[], dependents: number[]): AddItemAction => ({
	type: DownloadManagerActionKeys.ADD_ITEM,
	id: driveItem.id,
	mode,
	dependencies,
	dependents,
	version: driveItem.version,
});

const updateDownloadStatusInManager = (id: number, status: DownloadStatus): UpdateStatusAction => ({
	type: DownloadManagerActionKeys.UPDATE_STATUS,
	id,
	status
});

const updateDownloadInManager = (driveItem: Api.DriveItem, mode: DownloadMode, dependencies: number[], dependents: number[]): UpdateDownloadAction => ({
	type: DownloadManagerActionKeys.UPDATE_DOWNLOAD,
	id: driveItem.id,
	mode,
	dependencies,
	dependents,
	version: driveItem.version,
});

const setRequestId = (id: number, requestId: number): SetRequestIdAction => ({
	type: DownloadManagerActionKeys.SET_REQUEST_ID,
	id,
	requestId
});

const removeDownloadFromManager = (id: number): RemoveDownloadAction => ({
	type: DownloadManagerActionKeys.REMOVE_DOWNLOAD,
	id
});

const addItem = (driveItem: Api.DriveItem, downloadMode: DownloadMode, dependencies: number[], dependents: number[]) =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(addItemInManager(driveItem, downloadMode, dependencies, dependents));
		dispatch(updateLocalStorage());
	};

const updateDownloadStatus = (id: number, status: DownloadStatus) =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(updateDownloadStatusInManager(id, status));
		dispatch(updateLocalStorage());
	};

const updateDownload = (driveItem: Api.DriveItem, downloadMode: DownloadMode, dependencies: number[], dependents: number[]) =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(updateDownloadInManager(driveItem, downloadMode, dependencies, dependents));
		dispatch(updateLocalStorage());
	};

const removeDownloadItem = (id: number) =>
	(dispatch: Dispatch<StoreState>): void => {
		dispatch(removeDownloadFromManager(id));
		dispatch(updateLocalStorage());
	};

const updateLocalStorage = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	let downloadsToSave: LocalStorageDownloadInfos[] = [];
	const { downloadManager } = getState();
	const { items } = downloadManager;
	Object.keys(items).forEach(key => {
		const id = parseInt(key, 10);
		const item: DownloadManagerItemState = items[id];

		if (item.mode !== DownloadMode.Temp) {
			downloadsToSave.push({
				id,
				version: item.version,
				mode: item.mode,
				status: item.status,
				dependencies: item.dependencies,
				dependents: item.dependents
			});
		}
	});
	LocalStorageManager.setDownloads(downloadsToSave);
};

interface DependencyInfo {
	fromDownloadManager: DownloadManagerItemState;
	fromDriveList: DriveItemInfo;
}

const removeDependentFromDependencies = (driveItem: Api.DriveItem) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
		const driveItemInDownloadManager = getState().downloadManager.items[driveItem.id];
		const dependenciesId = driveItemInDownloadManager.dependencies;
		let dependenciesInfo: {[key: number]: DependencyInfo} = {};
		dependenciesId.forEach((dependencyId) => {
			dependenciesInfo[dependencyId] = {
				fromDownloadManager: getState().downloadManager.items[dependencyId],
				fromDriveList: getState().driveList.driveItems[dependencyId],
			};
		});

		for (const dependencyId in dependenciesInfo) {
			if (dependenciesInfo.hasOwnProperty(dependencyId)) {
				const { fromDriveList, fromDownloadManager } = dependenciesInfo[dependencyId];
				if (fromDownloadManager !== undefined && fromDriveList !== undefined) { // prevent infinite loop when a dependency and its parent are mutually dependent of each other
					const newDependentsId = fromDownloadManager.dependents.filter((dependent) => dependent !== driveItem.id);
					dispatch(updateDownload(fromDriveList, fromDownloadManager.mode, fromDownloadManager.dependencies, newDependentsId));
				}
			}
		}
	};

const transformTemporaryDownload = (driveItem: Api.DriveItem, itemFromDownloadManager: DownloadManagerItemState, dependentId?: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void> => {
		const fileName = `${driveItem.id}.${Utils.getFileExtension(driveItem.originalFile)}`;
		const { dependencies, dependents } = itemFromDownloadManager;
		const downloadMode = dependentId === undefined ? DownloadMode.Requested : DownloadMode.Dependency;

		let newDependents = [...dependents];
		if (dependentId !== undefined) {
			newDependents = [...dependents, dependentId];
		}

		return NativeApi.moveFile(`${tempFolder}/${fileName}`, `${downloadsFolder}/${fileName}`)
			.then((result) => {
				if (!result) {
					dispatch(createNotification('error.download_fail', { id: driveItem.id }));
				} else {
					dispatch(updateDownload(driveItem, downloadMode, dependencies, newDependents));
				}
			})
		;
	};

const getMostImportantMode = (mode1: DownloadMode, mode2: DownloadMode) => {
	const orderOfImportance = [DownloadMode.Temp, DownloadMode.Dependency, DownloadMode.Requested, DownloadMode.Required];

	return orderOfImportance.indexOf(mode1) > orderOfImportance.indexOf(mode2) ? mode1 : mode2;
};

const updateDependents = (driveItem: Api.DriveItem, itemFromDownloadManager: DownloadManagerItemState, requestedMode: DownloadMode, dependentId?: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Q.Promise<void> => {
		const { dependencies, dependents, mode } = itemFromDownloadManager;
		let newDependents = [...dependents];
		if (dependentId !== undefined) {
			newDependents = [...dependents, dependentId];
		}
		const newMode = getMostImportantMode(mode, requestedMode);
		dispatch(updateDownload(driveItem, newMode, dependencies, newDependents));
		return Q.Promise.resolve({});
	};

const recordDownloadProgress = (progress: Progress, id: number) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { downloadManager } = getState();

	const lastProgress = downloadManager.items[id] ? downloadManager.items[id].progress : undefined;
	const lastRate = lastProgress && lastProgress.loaded && lastProgress.totalSize ? Math.round(lastProgress.loaded * 100 / lastProgress.totalSize) : 0;
	const currentRate = progress && progress.loaded && progress.totalSize ? Math.round(progress.loaded * 100 / progress.totalSize) : 0;

	if (lastRate !== currentRate) {
		dispatch({
			type: DownloadManagerActionKeys.RECORD_DOWNLOAD_PROGRESS,
			progress,
			id
		});
	}
};

export {
	loadPreviousDownloads, updateDownloadInManager,
	addItem, updateDownloadStatus, updateDownload, removeDownloadItem, setRequestId, recordDownloadProgress,
	removeDependentFromDependencies, transformTemporaryDownload, updateDependents, startDownloadAll, finishDownloadAll,
	setIsDeletingAllStatus, addItemInManager, setDownloadAllStatus
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DownloadManager/actions/store.ts